import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
} from 'reactstrap';
import { format, formatDistanceToNow, isValid, parseISO } from 'date-fns';
import { id as localeId } from 'date-fns/locale';

import { api } from '../../config';
import {
  frontendBuildInfo,
  isUnknown,
  shortCommit,
} from '../../utils/buildInfo';

const API_BASE = api.API_URL;
const VERSION_ENDPOINT = `${API_BASE.replace(/\/+$/, '')}/version`;

const parseDate = value => {
  if (!value || isUnknown(value)) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : null;
};

const formatAbsolute = value => {
  const parsed = parseDate(value);
  if (!parsed) return '—';
  return format(parsed, 'dd MMMM yyyy, HH:mm:ss', { locale: localeId });
};

const formatRelative = value => {
  const parsed = parseDate(value);
  if (!parsed) return null;
  return `${formatDistanceToNow(parsed, { locale: localeId })} lalu`;
};

const formatUptime = seconds => {
  if (typeof seconds !== 'number' || Number.isNaN(seconds)) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const parts = [];
  if (days) parts.push(`${days} hari`);
  if (hours) parts.push(`${hours} jam`);
  parts.push(`${minutes} menit`);
  return parts.join(' ');
};

const Field = ({ label, value, hint, mono }) => (
  <Row className='py-2 border-bottom'>
    <Col xs={5} className='text-muted small'>
      {label}
    </Col>
    <Col xs={7}>
      <span className={mono ? 'font-monospace' : undefined}>
        {value || '—'}
      </span>
      {hint ? <div className='text-muted small'>{hint}</div> : null}
    </Col>
  </Row>
);

const StatusBadge = ({ state }) => {
  if (state === 'loading') {
    return (
      <Badge color='secondary' pill>
        Memeriksa
      </Badge>
    );
  }
  if (state === 'error') {
    return (
      <Badge color='danger' pill>
        Tidak terjangkau
      </Badge>
    );
  }
  return (
    <Badge color='success' pill>
      Online
    </Badge>
  );
};

const SystemVersion = () => {
  const [backend, setBackend] = useState(null);
  const [backendState, setBackendState] = useState('loading');
  const [backendError, setBackendError] = useState('');
  const [checkedAt, setCheckedAt] = useState(null);

  const loadBackend = useCallback(async () => {
    setBackendState('loading');
    setBackendError('');
    try {
      const response = await fetch(VERSION_ENDPOINT, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status} ${response.statusText}`);
      }
      const payload = await response.json();
      setBackend(payload?.data ?? payload);
      setBackendState('ready');
    } catch (error) {
      setBackend(null);
      setBackendError(error?.message || 'Gagal menghubungi API');
      setBackendState('error');
    } finally {
      setCheckedAt(new Date());
    }
  }, []);

  useEffect(() => {
    document.title = 'Informasi Versi | Hydro Digital Procurement';
    loadBackend();
  }, [loadBackend]);

  const fe = frontendBuildInfo;

  return (
    <div className='page-content pt-4 pb-5'>
      <Container>
        <Row className='justify-content-center'>
          <Col xl={10}>
            <div className='d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3'>
              <div>
                <h4 className='mb-1'>Informasi Versi &amp; Update Terakhir</h4>
                <p className='text-muted mb-0'>
                  Menampilkan build yang sedang aktif untuk frontend dan backend
                  Hydro Digital Procurement.
                </p>
              </div>
              <Button
                color='primary'
                outline
                onClick={loadBackend}
                disabled={backendState === 'loading'}
              >
                {backendState === 'loading' ? (
                  <>
                    <Spinner size='sm' className='me-2' />
                    Memeriksa
                  </>
                ) : (
                  'Periksa Ulang'
                )}
              </Button>
            </div>

            <Row className='g-3'>
              <Col md={6}>
                <Card className='h-100'>
                  <CardBody>
                    <div className='d-flex align-items-center justify-content-between mb-3'>
                      <h5 className='mb-0'>Frontend</h5>
                      <Badge color='info' pill>
                        hydro-fe
                      </Badge>
                    </div>

                    <Field label='Versi' value={fe.version} mono />
                    <Field label='Build Number' value={fe.buildNumber} mono />
                    <Field
                      label='Commit'
                      value={shortCommit(fe.commit)}
                      hint={isUnknown(fe.commit) ? null : fe.commit}
                      mono
                    />
                    <Field label='Branch' value={fe.branch} mono />
                    <Field
                      label='Waktu Build'
                      value={formatAbsolute(fe.buildTime)}
                      hint={formatRelative(fe.buildTime)}
                    />
                    <Field label='Host' value={window.location.origin} mono />

                    {isUnknown(fe.buildNumber) ? (
                      <p className='text-muted small mt-3 mb-0'>
                        Metadata build kosong — build ini kemungkinan dijalankan
                        di lokal, bukan melalui pipeline Jenkins.
                      </p>
                    ) : null}
                  </CardBody>
                </Card>
              </Col>

              <Col md={6}>
                <Card className='h-100'>
                  <CardBody>
                    <div className='d-flex align-items-center justify-content-between mb-3'>
                      <h5 className='mb-0'>Backend</h5>
                      <StatusBadge state={backendState} />
                    </div>

                    {backendState === 'error' ? (
                      <div className='alert alert-danger mb-3' role='alert'>
                        Gagal mengambil data versi backend.
                        <div className='small mt-1'>{backendError}</div>
                      </div>
                    ) : null}

                    <Field label='Versi' value={backend?.version} mono />
                    <Field
                      label='Build Number'
                      value={backend?.buildNumber}
                      mono
                    />
                    <Field
                      label='Commit'
                      value={backend ? shortCommit(backend.commit) : null}
                      hint={
                        backend && !isUnknown(backend.commit)
                          ? backend.commit
                          : null
                      }
                      mono
                    />
                    <Field label='Branch' value={backend?.branch} mono />
                    <Field
                      label='Waktu Build'
                      value={formatAbsolute(backend?.buildTime)}
                      hint={formatRelative(backend?.buildTime)}
                    />
                    <Field
                      label='Terakhir Restart'
                      value={formatAbsolute(backend?.startedAt)}
                      hint={
                        backend
                          ? `Uptime ${formatUptime(backend.uptimeSeconds)}`
                          : null
                      }
                    />
                    <Field label='Environment' value={backend?.environment} />
                    <Field label='API' value={API_BASE} mono />
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <p className='text-muted small mt-3 mb-0'>
              Terakhir diperiksa:{' '}
              {checkedAt
                ? format(checkedAt, 'dd MMMM yyyy, HH:mm:ss', {
                    locale: localeId,
                  })
                : '—'}
            </p>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default SystemVersion;
